// Draws an ad on a canvas: 5 templates × 3 sizes. Text is typeset here from what the owner
// typed and chose; the AI never draws text into pixels.

import { FORMATS, type Format } from '@tangazo/shared';

type Ctx = CanvasRenderingContext2D;
type Img = CanvasImageSource & { naturalWidth?: number; naturalHeight?: number; width: number; height: number };
type Family = 'Anton' | 'Lexend';

export interface AdText { headline: string; subline: string; offer: string; cta: string }
export interface AdOptions {
  template: string;
  format: Format;
  accent: string;
  text: AdText;
  business?: string;
  location?: string;
  price?: string;
  phone?: string;
  photo?: Img | null;
  extraPhotos?: Img[];
  logo?: Img | null;
  labels: { call: string; offerWord: string };
  credit?: string | null;
  watermark?: string | null;
  scale?: number;
}
interface Ad extends AdOptions { w: number; h: number; top: number; bottom: number; compact: boolean; extraPhotos: Img[] }
interface Block { lines: string[]; size: number; lh: number; family: Family; weight: number; height: number }

const FAMILY: Record<Family, string> = {
  Anton: 'Anton, Impact, "Arial Narrow", sans-serif',
  Lexend: 'Lexend, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
};

export async function ensureFonts() {
  if (!document.fonts) return;
  await Promise.all([
    document.fonts.load('100px Anton'),
    document.fonts.load('400 40px Lexend'),
    document.fonts.load('700 40px Lexend'),
  ]).catch(() => {});
}

// ---------- helpers ----------

const fontOf = (size: number, family: Family = 'Lexend', weight = 400) => `${weight} ${Math.round(size)}px ${FAMILY[family]}`;

function hexToRgb(hex: string): [number, number, number] {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function luminance(hex: string) {
  const [r, g, b] = hexToRgb(hex).map((v) => {
    v /= 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

const onColor = (hex: string) => (luminance(hex) > 0.4 ? '#141414' : '#FFFFFF');

function shade(hex: string, amt: number) {
  const [r, g, b] = hexToRgb(hex).map((v) => Math.max(0, Math.min(255, Math.round(amt < 0 ? v * (1 + amt) : v + (255 - v) * amt))));
  return `rgb(${r},${g},${b})`;
}

function rgba(hex: string, a: number) {
  const [r, g, b] = hexToRgb(hex);
  return `rgba(${r},${g},${b},${a})`;
}

function rrect(ctx: Ctx, x: number, y: number, w: number, h: number, r: number) {
  r = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function cover(ctx: Ctx, img: Img | null | undefined, x: number, y: number, w: number, h: number, accent = '#888888') {
  if (!img) {
    const g = ctx.createLinearGradient(x, y, x + w, y + h);
    g.addColorStop(0, shade(accent, 0.5));
    g.addColorStop(1, shade(accent, -0.3));
    ctx.fillStyle = g;
    ctx.fillRect(x, y, w, h);
    return;
  }
  const iw = img.naturalWidth || img.width;
  const ih = img.naturalHeight || img.height;
  const s = Math.max(w / iw, h / ih);
  const sw = w / s;
  const sh = h / s;
  ctx.drawImage(img, (iw - sw) / 2, (ih - sh) / 2, sw, sh, x, y, w, h);
}

function contain(ctx: Ctx, img: Img, x: number, y: number, w: number, h: number) {
  const iw = img.naturalWidth || img.width;
  const ih = img.naturalHeight || img.height;
  const s = Math.min(w / iw, h / ih);
  ctx.drawImage(img, x + (w - iw * s) / 2, y + (h - ih * s) / 2, iw * s, ih * s);
}

function wrapLines(ctx: Ctx, text: string, maxWidth: number) {
  const words = String(text || '').split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let line = '';
  for (const w of words) {
    const t = line ? `${line} ${w}` : w;
    if (!line || ctx.measureText(t).width <= maxWidth) line = t;
    else {
      lines.push(line);
      line = w;
    }
  }
  if (line) lines.push(line);
  return lines;
}

function ellipsize(ctx: Ctx, s: string, max: number) {
  if (ctx.measureText(s).width <= max) return s;
  while (s.length > 1 && ctx.measureText(s + '…').width > max) s = s.slice(0, -1);
  return s.trimEnd() + '…';
}

// Largest size at which `text` fits in maxLines lines of maxWidth.
function fit(
  ctx: Ctx,
  text: string | undefined,
  { family = 'Lexend', weight = 400, maxWidth, maxSize, minSize, maxLines = 2, lh = 1.12, upper = false }:
    { family?: Family; weight?: number; maxWidth: number; maxSize: number; minSize: number; maxLines?: number; lh?: number; upper?: boolean },
): Block {
  const t = String(text || '').trim();
  const s = upper ? t.toLocaleUpperCase('sw') : t;
  const block: Block = { lines: [], size: maxSize, lh, family, weight, height: 0 };
  if (!s) return block;
  for (let size = maxSize; size >= minSize; size -= 2) {
    ctx.font = fontOf(size, family, weight);
    const lines = wrapLines(ctx, s, maxWidth);
    if (lines.length <= maxLines && lines.every((l) => ctx.measureText(l).width <= maxWidth)) {
      return { ...block, lines, size, height: lines.length * size * lh };
    }
  }
  ctx.font = fontOf(minSize, family, weight);
  const all = wrapLines(ctx, s, maxWidth);
  const lines = all.slice(0, maxLines).map((l, i, arr) =>
    ellipsize(ctx, i === arr.length - 1 && all.length > maxLines ? l + '…' : l, maxWidth));
  return { ...block, lines, size: minSize, height: lines.length * minSize * lh };
}

function draw(
  ctx: Ctx,
  block: Block,
  x: number,
  y: number,
  { align = 'left', color = '#fff', glow = null, stroke = null }: { align?: CanvasTextAlign; color?: string; glow?: string | null; stroke?: string | null } = {},
) {
  if (!block.lines.length) return y;
  ctx.save();
  ctx.font = fontOf(block.size, block.family, block.weight);
  ctx.textAlign = align;
  ctx.textBaseline = 'middle';
  ctx.fillStyle = color;
  if (glow) {
    ctx.shadowColor = glow;
    ctx.shadowBlur = block.size * 0.35;
  }
  block.lines.forEach((line, i) => {
    const cy = y + block.size * block.lh * (i + 0.5);
    if (stroke) {
      ctx.lineWidth = block.size * 0.08;
      ctx.strokeStyle = stroke;
      ctx.lineJoin = 'round';
      ctx.strokeText(line, x, cy);
    }
    ctx.fillText(line, x, cy);
  });
  ctx.restore();
  return y + block.height;
}

function sticker(ctx: Ctx, cx: number, cy: number, r: number, text: string | undefined, bg: string, rot = -0.2) {
  if (!text) return;
  ctx.save();
  ctx.translate(cx, cy);
  ctx.rotate(rot);
  ctx.shadowColor = 'rgba(0,0,0,0.3)';
  ctx.shadowBlur = 24;
  ctx.fillStyle = bg;
  ctx.beginPath();
  ctx.arc(0, 0, r, 0, Math.PI * 2);
  ctx.fill();
  ctx.shadowColor = 'transparent';
  ctx.strokeStyle = 'rgba(255,255,255,0.9)';
  ctx.lineWidth = r * 0.05;
  ctx.beginPath();
  ctx.arc(0, 0, r * 0.88, 0, Math.PI * 2);
  ctx.stroke();
  const b = fit(ctx, text, { family: 'Anton', maxWidth: r * 1.45, maxSize: r * 0.5, minSize: r * 0.2, maxLines: 3, lh: 1.02, upper: true });
  draw(ctx, b, 0, -b.height / 2, { align: 'center', color: onColor(bg) });
  ctx.restore();
}

function pill(
  ctx: Ctx,
  x: number,
  y: number,
  text: string | null | undefined,
  { size, bg, fg, padX = size * 0.6, padY = size * 0.35, align = 'left', outline = false, maxWidth = 2000 }:
    { size: number; bg: string; fg: string; padX?: number; padY?: number; align?: 'left' | 'center' | 'right'; outline?: boolean; maxWidth?: number },
) {
  if (!text) return 0;
  ctx.save();
  ctx.font = fontOf(size, 'Lexend', 700);
  const label = ellipsize(ctx, text, maxWidth - padX * 2);
  const w = ctx.measureText(label).width + padX * 2;
  const h = size + padY * 2;
  const left = align === 'center' ? x - w / 2 : align === 'right' ? x - w : x;
  rrect(ctx, left, y, w, h, h / 2);
  if (outline) {
    ctx.lineWidth = size * 0.12;
    ctx.strokeStyle = bg;
    ctx.stroke();
  } else {
    ctx.fillStyle = bg;
    ctx.fill();
  }
  ctx.fillStyle = fg;
  ctx.textBaseline = 'middle';
  ctx.textAlign = 'center';
  ctx.fillText(label, left + w / 2, y + h / 2 + size * 0.04);
  ctx.restore();
  return h;
}

// Button-like bar: CTA and phone number.
function ctaBar(ctx: Ctx, x: number, y: number, w: number, h: number, ad: Ad, { bg, fg, radius = h / 2 }: { bg: string; fg: string; radius?: number }) {
  ctx.save();
  rrect(ctx, x, y, w, h, radius);
  ctx.fillStyle = bg;
  ctx.fill();
  const phone = ad.phone || '';
  const lead = ad.text.cta || ad.labels.call;
  const label = phone ? `${lead} · ${phone}` : lead;
  const b = fit(ctx, label, { family: 'Lexend', weight: 700, maxWidth: w - h * 0.6, maxSize: h * 0.4, minSize: h * 0.22, maxLines: 1 });
  draw(ctx, b, x + w / 2, y + (h - b.height) / 2, { align: 'center', color: fg });
  ctx.restore();
}

function brandRow(
  ctx: Ctx,
  ad: Ad,
  x: number,
  y: number,
  { size = 44, color = '#fff', align = 'left', maxWidth = 700, logoBg = '#fff' }: { size?: number; color?: string; align?: 'left' | 'center'; maxWidth?: number; logoBg?: string },
) {
  let tx = x;
  const logoSize = size * 1.9;
  if (ad.logo) {
    const lx = align === 'center' ? x - maxWidth / 2 : x;
    ctx.save();
    ctx.beginPath();
    ctx.arc(lx + logoSize / 2, y + logoSize / 2, logoSize / 2, 0, Math.PI * 2);
    ctx.fillStyle = logoBg;
    ctx.fill();
    ctx.clip();
    contain(ctx, ad.logo, lx + logoSize * 0.12, y + logoSize * 0.12, logoSize * 0.76, logoSize * 0.76);
    ctx.restore();
    if (align === 'left') tx = x + logoSize + size * 0.4;
  }
  const name = fit(ctx, ad.business, { family: 'Lexend', weight: 700, maxWidth: maxWidth - (tx - x), maxSize: size, minSize: size * 0.6, maxLines: 1 });
  const loc = fit(ctx, ad.location, { family: 'Lexend', weight: 400, maxWidth: maxWidth - (tx - x), maxSize: size * 0.7, minSize: size * 0.5, maxLines: 1 });
  const textH = name.height + (loc.lines.length ? loc.height + size * 0.1 : 0);
  const rowH = Math.max(ad.logo && align === 'left' ? logoSize : 0, textH);
  let ty = y + (rowH - textH) / 2;
  if (align === 'center' && ad.logo) ty = y + logoSize + size * 0.3;
  ty = draw(ctx, name, tx, ty, { align, color });
  if (loc.lines.length) draw(ctx, loc, tx, ty + size * 0.1, { align, color, stroke: null });
  return align === 'center' && ad.logo ? logoSize + size * 0.3 + textH : rowH;
}

// ---------- templates ----------

const TEMPLATES: Record<string, (ctx: Ctx, ad: Ad) => void> = {
  // Full photo, big headline, price sticker.
  bango(ctx, ad) {
    const { w, h, top, bottom, accent } = ad;
    cover(ctx, ad.photo, 0, 0, w, h, accent);
    let g = ctx.createLinearGradient(0, 0, 0, top + 300);
    g.addColorStop(0, 'rgba(0,0,0,0.6)');
    g.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, w, top + 300);
    g = ctx.createLinearGradient(0, h * 0.38, 0, h);
    g.addColorStop(0, 'rgba(0,0,0,0)');
    g.addColorStop(0.55, 'rgba(0,0,0,0.7)');
    g.addColorStop(1, 'rgba(0,0,0,0.9)');
    ctx.fillStyle = g;
    ctx.fillRect(0, h * 0.38, w, h * 0.62);

    const pad = 64;
    brandRow(ctx, ad, pad, top + 10, { size: 44, maxWidth: w - pad * 2 - 300 });
    const r = ad.compact ? 120 : 140;
    sticker(ctx, w - pad - r, top + r + 10, r, ad.price || ad.text.offer, accent, -0.2);

    let y = bottom - 120;
    ctaBar(ctx, pad, y, w - pad * 2, 120, ad, { bg: accent, fg: onColor(accent) });
    const sub = fit(ctx, ad.text.subline, { maxWidth: w - pad * 2, maxSize: 46, minSize: 32, maxLines: ad.compact ? 1 : 2, lh: 1.25 });
    y -= 36 + sub.height;
    draw(ctx, sub, pad, y, { color: 'rgba(255,255,255,0.92)' });
    const head = fit(ctx, ad.text.headline, { family: 'Anton', maxWidth: w - pad * 2, maxSize: ad.compact ? 128 : 156, minSize: 64, maxLines: 3, lh: 1.02, upper: true });
    y -= 20 + head.height;
    draw(ctx, head, pad, y, { color: '#fff' });
    if (ad.price && ad.text.offer) pill(ctx, pad, y - 96, ad.text.offer, { size: 40, bg: accent, fg: onColor(accent) });
  },

  // Sale banner, framed photo.
  ofa(ctx, ad) {
    const { w, h, top, bottom, accent } = ad;
    const fg = onColor(accent);
    ctx.fillStyle = accent;
    ctx.fillRect(0, 0, w, h);
    ctx.save();
    ctx.strokeStyle = fg === '#FFFFFF' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.07)';
    ctx.lineWidth = 36;
    for (let x = -h; x < w; x += 110) {
      ctx.beginPath();
      ctx.moveTo(x, h);
      ctx.lineTo(x + h, 0);
      ctx.stroke();
    }
    ctx.restore();

    const pad = 60;
    let y = top + 6;
    y += brandRow(ctx, ad, w / 2, y, { size: 40, color: fg, align: 'center', maxWidth: w - pad * 2 }) + 26;

    const bandH = ad.compact ? 140 : 180;
    ctx.save();
    ctx.translate(w / 2, y + bandH / 2);
    ctx.rotate(-0.03);
    ctx.fillStyle = '#141414';
    ctx.fillRect(-w / 2 - 40, -bandH / 2, w + 80, bandH);
    const word = fit(ctx, ad.text.offer || ad.labels.offerWord, { family: 'Anton', maxWidth: w - 160, maxSize: bandH * 0.72, minSize: 50, maxLines: 1, upper: true });
    draw(ctx, word, 0, -word.height / 2, { align: 'center', color: luminance(accent) < 0.08 ? '#FFD23F' : accent });
    ctx.restore();
    y += bandH + 40;

    const ctaH = 112;
    const head = fit(ctx, ad.text.headline, { family: 'Anton', maxWidth: w - pad * 2, maxSize: ad.compact ? 92 : 116, minSize: 56, maxLines: 2, lh: 1.04, upper: true });
    const sub = fit(ctx, ad.text.subline, { maxWidth: w - pad * 2, maxSize: 42, minSize: 30, maxLines: ad.compact ? 1 : 2, lh: 1.25 });
    const below = head.height + 18 + sub.height + 34 + ctaH;
    const frameH = Math.max(240, bottom - y - below - 40);
    const frameW = Math.min(w - 200, frameH * 1.25);
    ctx.save();
    ctx.translate(w / 2, y + frameH / 2);
    ctx.rotate(0.025);
    ctx.shadowColor = 'rgba(0,0,0,0.35)';
    ctx.shadowBlur = 40;
    ctx.fillStyle = '#fff';
    ctx.fillRect(-frameW / 2 - 18, -frameH / 2 - 18, frameW + 36, frameH + 36);
    ctx.shadowColor = 'transparent';
    cover(ctx, ad.photo, -frameW / 2, -frameH / 2, frameW, frameH, accent);
    ctx.restore();
    if (ad.price) {
      const r = ad.compact ? 100 : 120;
      sticker(ctx, w / 2 + frameW / 2 - r * 0.4, y + frameH - r * 0.3, r, ad.price, '#FFFFFF', 0.18);
    }
    y += frameH + 40;
    y = draw(ctx, head, w / 2, y, { align: 'center', color: fg });
    draw(ctx, sub, w / 2, y + 18, { align: 'center', color: fg });
    ctaBar(ctx, pad, bottom - ctaH, w - pad * 2, ctaH, ad, { bg: '#141414', fg: '#fff', radius: 18 });
  },

  // Kanga-style border with the message in a panel.
  kanga(ctx, ad) {
    const { w, h, top, bottom, accent } = ad;
    const dark = '#1B1B1B';
    const light = shade(accent, 0.55);
    ctx.fillStyle = accent;
    ctx.fillRect(0, 0, w, h);
    const bw = 66;
    // pindo: the patterned border
    ctx.fillStyle = dark;
    ctx.fillRect(0, 0, w, bw);
    ctx.fillRect(0, h - bw, w, bw);
    ctx.fillRect(0, 0, bw, h);
    ctx.fillRect(w - bw, 0, bw, h);
    const step = 66;
    ctx.fillStyle = light;
    const motif = (x: number, y: number, i: number) => {
      ctx.beginPath();
      if (i % 2) ctx.arc(x, y, 14, 0, Math.PI * 2);
      else {
        ctx.moveTo(x, y - 20);
        ctx.lineTo(x + 20, y);
        ctx.lineTo(x, y + 20);
        ctx.lineTo(x - 20, y);
        ctx.closePath();
      }
      ctx.fill();
    };
    let i = 0;
    for (let x = bw / 2; x < w; x += step, i++) {
      motif(x, bw / 2, i);
      motif(x, h - bw / 2, i);
    }
    i = 0;
    for (let y = bw * 1.5; y < h - bw; y += step, i++) {
      motif(bw / 2, y, i);
      motif(w - bw / 2, y, i);
    }
    ctx.strokeStyle = light;
    ctx.lineWidth = 6;
    ctx.strokeRect(bw + 14, bw + 14, w - (bw + 14) * 2, h - (bw + 14) * 2);
    // mji: the centre, a dotted field
    ctx.fillStyle = rgba('#FFFFFF', 0.12);
    for (let y = bw + 50; y < h - bw - 30; y += 44) {
      for (let x = bw + 50 + ((y / 44) % 2) * 22; x < w - bw - 30; x += 44) {
        ctx.beginPath();
        ctx.arc(x, y, 5, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    const inner = bw + 50;
    const fg = onColor(accent);
    const t = Math.max(top, inner);
    const b = Math.min(bottom, h - inner);
    let y = t + brandRow(ctx, ad, w / 2, t, { size: 42, color: fg, align: 'center', maxWidth: w - inner * 2 }) + 30;

    const panelPad = 34;
    const head = fit(ctx, ad.text.headline, { family: 'Anton', maxWidth: w - inner * 2 - panelPad * 2, maxSize: ad.compact ? 84 : 104, minSize: 52, maxLines: 2, lh: 1.05, upper: true });
    const sub = fit(ctx, ad.text.subline, { maxWidth: w - inner * 2 - panelPad * 2, maxSize: 38, minSize: 28, maxLines: ad.compact ? 1 : 2, lh: 1.25 });
    const panelH = panelPad * 2 + head.height + (sub.lines.length ? 14 + sub.height : 0);
    const ctaH = 100;
    const photoH = Math.max(220, b - y - panelH - ctaH - 70);
    const photoW = w - inner * 2;
    ctx.save();
    rrect(ctx, inner, y, photoW, photoH, 28);
    ctx.clip();
    cover(ctx, ad.photo, inner, y, photoW, photoH, accent);
    ctx.restore();
    ctx.lineWidth = 10;
    ctx.strokeStyle = dark;
    rrect(ctx, inner, y, photoW, photoH, 28);
    ctx.stroke();
    if (ad.price || ad.text.offer) sticker(ctx, w - inner - 90, y + 100, 105, ad.price || ad.text.offer, dark, 0.15);
    y += photoH + 34;
    // jina: the message panel
    ctx.fillStyle = '#FFF8EC';
    rrect(ctx, inner, y, photoW, panelH, 16);
    ctx.fill();
    ctx.strokeStyle = dark;
    ctx.lineWidth = 6;
    ctx.stroke();
    let py = draw(ctx, head, w / 2, y + panelPad, { align: 'center', color: dark });
    if (sub.lines.length) draw(ctx, sub, w / 2, py + 14, { align: 'center', color: '#333' });
    ctaBar(ctx, inner, b - ctaH, photoW, ctaH, ad, { bg: dark, fg: '#fff' });
  },

  // Clean white, for salons and boutiques.
  safi(ctx, ad) {
    const { w, h, top, bottom, accent } = ad;
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, w, h);
    ctx.fillStyle = rgba(accent, 0.08);
    ctx.beginPath();
    ctx.arc(w * 0.95, h * 0.08, w * 0.45, 0, Math.PI * 2);
    ctx.fill();

    const pad = 70;
    let y = top + 10;
    y += brandRow(ctx, ad, pad, y, { size: 42, color: '#1A1A1A', maxWidth: w - pad * 2, logoBg: '#F2F2F2' }) + 22;
    ctx.fillStyle = accent;
    ctx.fillRect(pad, y, 120, 8);
    y += 40;

    const head = fit(ctx, ad.text.headline, { family: 'Lexend', weight: 700, maxWidth: w - pad * 2, maxSize: ad.compact ? 74 : 92, minSize: 46, maxLines: 2, lh: 1.12 });
    const sub = fit(ctx, ad.text.subline, { maxWidth: w - pad * 2, maxSize: 40, minSize: 28, maxLines: ad.compact ? 1 : 2, lh: 1.3 });
    const ctaH = 110;
    const priceH = ad.price ? 100 : 0;
    const thumbs = ad.compact ? [] : ad.extraPhotos.slice(0, 3);
    const thumbH = thumbs.length ? 180 : 0;
    const below = head.height + 16 + sub.height + 30 + priceH + 30 + ctaH;
    const photoH = Math.max(220, bottom - y - below - 40 - (thumbH ? thumbH + 20 : 0));
    ctx.save();
    rrect(ctx, pad, y, w - pad * 2, photoH, 40);
    ctx.clip();
    cover(ctx, ad.photo, pad, y, w - pad * 2, photoH, accent);
    ctx.restore();
    if (ad.text.offer) pill(ctx, pad + 30, y + 30, ad.text.offer, { size: 36, bg: '#FFFFFF', fg: '#1A1A1A' });
    y += photoH + 20;
    if (thumbH) {
      const tw = (w - pad * 2 - 20 * (thumbs.length - 1)) / thumbs.length;
      thumbs.forEach((img, i) => {
        ctx.save();
        rrect(ctx, pad + i * (tw + 20), y, tw, thumbH, 24);
        ctx.clip();
        cover(ctx, img, pad + i * (tw + 20), y, tw, thumbH, accent);
        ctx.restore();
      });
      y += thumbH + 20;
    }
    y += 20;
    y = draw(ctx, head, pad, y, { color: '#1A1A1A' });
    y = draw(ctx, sub, pad, y + 16, { color: '#555' });
    if (ad.price) {
      const pb = fit(ctx, ad.price, { family: 'Anton', maxWidth: w - pad * 2, maxSize: 86, minSize: 50, maxLines: 1 });
      draw(ctx, pb, pad, bottom - ctaH - 30 - pb.height, { color: accent });
    }
    ctaBar(ctx, pad, bottom - ctaH, w - pad * 2, ctaH, ad, { bg: accent, fg: onColor(accent), radius: 24 });
  },

  // Dark with a glow, for phones and electronics.
  usiku(ctx, ad) {
    const { w, h, top, bottom, accent } = ad;
    const bg = ctx.createLinearGradient(0, 0, 0, h);
    bg.addColorStop(0, '#0B1020');
    bg.addColorStop(1, '#020308');
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, w, h);
    const pad = 64;
    let y = top + 10;
    y += brandRow(ctx, ad, w / 2, y, { size: 40, color: '#fff', align: 'center', maxWidth: w - pad * 2, logoBg: '#fff' }) + 40;

    const head = fit(ctx, ad.text.headline, { family: 'Anton', maxWidth: w - pad * 2, maxSize: ad.compact ? 100 : 128, minSize: 56, maxLines: 2, lh: 1.04, upper: true });
    const sub = fit(ctx, ad.text.subline, { maxWidth: w - pad * 2, maxSize: 40, minSize: 28, maxLines: ad.compact ? 1 : 2, lh: 1.3 });
    const ctaH = 112;
    const priceH = ad.price ? 120 : 0;
    const below = head.height + 18 + sub.height + 30 + priceH + 20 + ctaH;
    const photoH = Math.max(220, bottom - y - below - 40);
    const photoW = Math.min(w - pad * 2, photoH * 1.1);
    const px = (w - photoW) / 2;
    const glow = ctx.createRadialGradient(w / 2, y + photoH / 2, 10, w / 2, y + photoH / 2, photoW * 0.85);
    glow.addColorStop(0, rgba(accent, 0.55));
    glow.addColorStop(1, rgba(accent, 0));
    ctx.fillStyle = glow;
    ctx.fillRect(0, y - photoH * 0.3, w, photoH * 1.6);
    ctx.save();
    ctx.shadowColor = accent;
    ctx.shadowBlur = 70;
    rrect(ctx, px, y, photoW, photoH, 36);
    ctx.fillStyle = '#000';
    ctx.fill();
    ctx.restore();
    ctx.save();
    rrect(ctx, px, y, photoW, photoH, 36);
    ctx.clip();
    cover(ctx, ad.photo, px, y, photoW, photoH, accent);
    ctx.restore();
    if (ad.text.offer) pill(ctx, w / 2, y - 30, ad.text.offer, { size: 34, bg: accent, fg: onColor(accent), align: 'center' });
    y += photoH + 40;
    y = draw(ctx, head, w / 2, y, { align: 'center', color: '#fff', glow: accent });
    y = draw(ctx, sub, w / 2, y + 18, { align: 'center', color: 'rgba(255,255,255,0.8)' });
    if (ad.price) {
      const pb = fit(ctx, ad.price, { family: 'Anton', maxWidth: w - pad * 2, maxSize: 96, minSize: 50, maxLines: 1 });
      draw(ctx, pb, w / 2, bottom - ctaH - 24 - pb.height, { align: 'center', color: accent, glow: accent });
    }
    ctaBar(ctx, pad, bottom - ctaH, w - pad * 2, ctaH, ad, { bg: accent, fg: onColor(accent) });
  },
};

function drawCredit(ctx: Ctx, ad: Ad) {
  const { w, h, format } = ad;
  const y = format === 'status' ? h * 0.845 : h - 54;
  pill(ctx, w / 2, y, ad.credit, { size: 24, bg: 'rgba(0,0,0,0.55)', fg: '#fff', align: 'center', padY: 10 });
}

function drawWatermark(ctx: Ctx, ad: Ad) {
  const { w, h } = ad;
  ctx.save();
  ctx.translate(w / 2, h / 2);
  ctx.rotate(-Math.PI / 6);
  ctx.font = fontOf(110, 'Anton');
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.lineWidth = 4;
  ctx.strokeStyle = 'rgba(0,0,0,0.25)';
  ctx.fillStyle = 'rgba(255,255,255,0.38)';
  const span = Math.hypot(w, h);
  let row = 0;
  for (let y = -span / 2; y < span / 2; y += 250, row++) {
    for (let x = -span / 2 + (row % 2) * 240; x < span / 2; x += 480) {
      ctx.strokeText(ad.watermark!, x, y);
      ctx.fillText(ad.watermark!, x, y);
    }
  }
  ctx.restore();
}

/**
 * opts: template, format, accent, text {headline, subline, offer, cta}, business, location,
 * price (already formatted), phone (display form), photo, extraPhotos[], logo,
 * labels {call, offerWord}, credit (string|null), watermark (string|null), scale (0.5 for preview)
 */
export function drawAd(canvas: HTMLCanvasElement, opts: AdOptions) {
  const { w, h } = FORMATS[opts.format] || FORMATS.status;
  const scale = opts.scale || 1;
  canvas.width = Math.round(w * scale);
  canvas.height = Math.round(h * scale);
  const ctx = canvas.getContext('2d')!;
  ctx.setTransform(scale, 0, 0, scale, 0, 0);
  ctx.imageSmoothingQuality = 'high';
  const status = opts.format === 'status';
  const ad: Ad = {
    ...opts,
    w,
    h,
    text: opts.text || {},
    extraPhotos: opts.extraPhotos || [],
    compact: h / w < 1.3,
    // WhatsApp Status covers the top 14% and bottom 16% with its own controls.
    top: status ? Math.round(h * 0.14) : 60,
    bottom: status ? Math.round(h * 0.84) : h - (opts.credit ? 100 : 60),
  };
  (TEMPLATES[opts.template] || TEMPLATES.bango)(ctx, ad);
  if (opts.credit) drawCredit(ctx, ad);
  if (opts.watermark) drawWatermark(ctx, ad);
  return canvas;
}

export function canvasToBlob(canvas: HTMLCanvasElement, type = 'image/jpeg', quality = 0.92): Promise<Blob> {
  return new Promise((resolve, reject) =>
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('toBlob failed'))), type, quality));
}
